import React from 'react';
import { ColorTheme, FontTheme, RadiusTheme, ShadowTheme } from 'types/theme';
import CircleTimer, { CircleTimerProps } from './CircleTimer';
import Icon, { IconProps } from './Icon';

/** Tailwind JIT가 소스에서 전체 클래스명을 인식하도록 리터럴 맵 사용 */
const BG_CLASS_BY_COLOR: Record<ColorTheme, string> = {
  'base-bg-default': 'bg-base-bg-default',
  'base-bg-subtle': 'bg-base-bg-subtle',
  'base-bg-strong': 'bg-base-bg-strong',
  'base-bg-focus': 'bg-base-bg-focus',
  'base-txt-primary': 'bg-base-txt-primary',
  'base-txt-secondary': 'bg-base-txt-secondary',
  'base-txt-tertiary': 'bg-base-txt-tertiary',
  'base-txt-disabled': 'bg-base-txt-disabled',
  'btn-bg-primary': 'bg-btn-bg-primary',
  'btn-bg-secondary': 'bg-btn-bg-secondary',
  'btn-bg-normal': 'bg-btn-bg-normal',
  'btn-bg-alert': 'bg-btn-bg-alert',
  'btn-bg-disabled': 'bg-btn-bg-disabled',
  'btn-txt-primary': 'bg-btn-txt-primary',
  'btn-txt-secondary': 'bg-btn-txt-secondary',
  'btn-txt-normal': 'bg-btn-txt-normal',
  'btn-txt-alert': 'bg-btn-txt-alert',
  'btn-txt-disabled': 'bg-btn-txt-disabled',
  'btn-txt-only': 'bg-btn-txt-only',
  'dialog-bg-default': 'bg-dialog-bg-default',
  black: 'bg-black',
  white: 'bg-white',
};

const TEXT_CLASS_BY_COLOR: Record<ColorTheme, string> = {
  'base-bg-default': 'text-base-bg-default',
  'base-bg-subtle': 'text-base-bg-subtle',
  'base-bg-strong': 'text-base-bg-strong',
  'base-bg-focus': 'text-base-bg-focus',
  'base-txt-primary': 'text-base-txt-primary',
  'base-txt-secondary': 'text-base-txt-secondary',
  'base-txt-tertiary': 'text-base-txt-tertiary',
  'base-txt-disabled': 'text-base-txt-disabled',
  'btn-bg-primary': 'text-btn-bg-primary',
  'btn-bg-secondary': 'text-btn-bg-secondary',
  'btn-bg-normal': 'text-btn-bg-normal',
  'btn-bg-alert': 'text-btn-bg-alert',
  'btn-bg-disabled': 'text-btn-bg-disabled',
  'btn-txt-primary': 'text-btn-txt-primary',
  'btn-txt-secondary': 'text-btn-txt-secondary',
  'btn-txt-normal': 'text-btn-txt-normal',
  'btn-txt-alert': 'text-btn-txt-alert',
  'btn-txt-disabled': 'text-btn-txt-disabled',
  'btn-txt-only': 'text-btn-txt-only',
  'dialog-bg-default': 'text-dialog-bg-default',
  black: 'text-black',
  white: 'text-white',
};

const BORDER_COLOR_CLASS_BY_THEME: Record<ColorTheme, string> = {
  'base-bg-default': 'border-base-bg-default',
  'base-bg-subtle': 'border-base-bg-subtle',
  'base-bg-strong': 'border-base-bg-strong',
  'base-bg-focus': 'border-base-bg-focus',
  'base-txt-primary': 'border-base-txt-primary',
  'base-txt-secondary': 'border-base-txt-secondary',
  'base-txt-tertiary': 'border-base-txt-tertiary',
  'base-txt-disabled': 'border-base-txt-disabled',
  'btn-bg-primary': 'border-btn-bg-primary',
  'btn-bg-secondary': 'border-btn-bg-secondary',
  'btn-bg-normal': 'border-btn-bg-normal',
  'btn-bg-alert': 'border-btn-bg-alert',
  'btn-bg-disabled': 'border-btn-bg-disabled',
  'btn-txt-primary': 'border-btn-txt-primary',
  'btn-txt-secondary': 'border-btn-txt-secondary',
  'btn-txt-normal': 'border-btn-txt-normal',
  'btn-txt-alert': 'border-btn-txt-alert',
  'btn-txt-disabled': 'border-btn-txt-disabled',
  'btn-txt-only': 'border-btn-txt-only',
  'dialog-bg-default': 'border-dialog-bg-default',
  black: 'border-black',
  white: 'border-white',
};

/** theme RadiusTheme의 xm은 tailwind의 rounded-xs(--radius-xs)에 대응 */
const ROUNDED_CLASS_BY_RADIUS: Record<RadiusTheme, string> = {
  xm: 'rounded-xs',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
};

/** Tailwind 기본 border 두께 유틸 */
const BORDER_WIDTH_CLASS: Record<number, string> = {
  0: 'border-0',
  1: 'border',
  2: 'border-2',
  4: 'border-4',
  8: 'border-8',
};

export interface ButtonStyle {
  text?: string;
  type?: 'outline' | 'solid' | 'text';
  color?: ColorTheme;
  fontColor?: ColorTheme;
  borderWidth?: number;
  borderRadius?: RadiusTheme;
  fontTheme?: FontTheme;
  background?: string;
  /** CSS 색상으로 글자색을 지정할 때 사용. 미지정 시 fontColor 테마 적용. */
  textColor?: string;
  icon?: IconProps;
  timer?: CircleTimerProps;
  trailing?: React.ReactNode;
  shadow?: ShadowTheme;
}

export interface ButtonProps {
  style: ButtonStyle;
  onClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
  className?: string;
}

const Button: React.FC<ButtonProps> = ({ style, onClick, className = '' }) => {
  const {
    text,
    type = 'solid',
    color = 'btn-bg-primary',
    borderWidth = 1,
    borderRadius = 'lg',
    fontTheme = 'typography-headline-lg',
    fontColor = 'white',
    background,
    textColor,
    icon,
    timer,
    trailing,
    shadow,
  } = style;

  const defaultClass = `flex flex-row items-center justify-center py-5 ${fontTheme} flex-1 max-h-[72px]`;
  const radiusClass = ROUNDED_CLASS_BY_RADIUS[borderRadius];
  const borderWidthClass = BORDER_WIDTH_CLASS[borderWidth] ?? 'border';
  const borderClass = `border-solid ${borderWidthClass} ${BORDER_COLOR_CLASS_BY_THEME[color]}`;
  const bgClass = background ? '' : BG_CLASS_BY_COLOR[color];
  const textClass = TEXT_CLASS_BY_COLOR[fontColor];
  const shadowClass = shadow ? `${shadow}` : '';

  // solid button
  if (type === 'solid')
    return (
      <div
        className={`${defaultClass} ${bgClass} ${radiusClass} ${textClass} ${className} ${shadowClass}`}
        onClick={onClick}
        style={{ background, color: textColor }}
      >
        {icon && <Icon {...icon} />}
        {timer && <CircleTimer {...timer} />}
        {text}
        {trailing}
      </div>
    );

  if (type === 'text') {
    return (
      <div className={`${defaultClass} ${className} ${textClass}`} onClick={onClick} style={{ color: textColor }}>
        {icon && <Icon {...icon} />}
        {timer && <CircleTimer {...timer} />}
        {text}
        {trailing}
      </div>
    );
  }

  // outline button
  return (
    <div className={`${defaultClass} ${borderClass} ${radiusClass} bg-white ${textClass} ${className}`} onClick={onClick} style={{ color: textColor }}>
      {icon && <Icon {...icon} />}
      {timer && <CircleTimer {...timer} />}
      {text}
      {trailing}
    </div>
  );
};

export default Button;
